<script setup>
import { computed, reactive, ref, watch } from "vue";
import { RouterView } from "vue-router";
import { useRoute } from "vue-router/composables";

import App from "@Components/App.vue";
import Popover from "@Popover/Index.vue";
import useLegacyStore from "@Composables/useLegacyStore";
import NotifyWrapper from "@Components/notify/NotifyWrapper.vue";
import OverlayWrapper from "@Components/popover/OverlayWrapper.vue";
import CookieConsent from "@Components/overhauling/consent/CookieConsent.vue";

import ApplicationNavbar from "./ApplicationNavbar.vue";
import ApplicationFooter from "./ApplicationFooter.vue";
import { NavbarVariant } from "./navbar-variant";

const route = useRoute();
const store = useLegacyStore();
const state = reactive({
  navbarVariant: NavbarVariant.Default,
  isNavbarVisible: true,
  isFooterVisible: true,
});

function onToggleNavbarVisibility(isVisible) {
  state.isNavbarVisible = isVisible;
}

function onToggleFooterVisibility(isVisible) {
  state.isFooterVisible = isVisible;
}

function onNavbarVariantSet(variant) {
  state.navbarVariant = variant;
}

const FLOATING_NAVBAR_VARIANTS = [ NavbarVariant.Ghost, NavbarVariant.Logo ];

// The floating variants are positioned absolutely, so the view has to be their containing block.
const isNavbarFloating = computed(() => FLOATING_NAVBAR_VARIANTS.includes(state.navbarVariant));

/**
 * @remarks TODO: As soon as legacy experience is removed, these computed properties won't be necessary.
 */
const AVAILABLE_OVERHAUL_PAGES_PAID = [
  "/app/pricing",
  "/app/features/",
  "/app/branded-domains",
  "/app/dev",
  "/app/proven-process",
  "/app/about-us",
  "/app/password/reset",
  "/app/privacy-manager",
  "/app/accessibility",
  "/app/terms",
  "/app/privacy-policy",
  "/app/cookies-policy",
  "/app/data-processing-agreement",
  "/app/black-friday-2025",
  "/app/cyber-monday-2025",
  "/app/organizations",
  "/app/contact-sales",
];

/**
 * The session as it stood when the current route was entered. Each shell renders its own
 * RouterView, so swapping shells while the route stays put mounts the page again. Logging
 * out clears the session before leaving the page, and a shell read live from the session
 * would mount that page a second time without one.
 */
const isUserGuest = ref(false);
const canUseAnalytics = ref(false);

function syncSession() {
  isUserGuest.value = !store?.getters.isUserLoggedIn;
  canUseAnalytics.value = store?.getters.canUseAnalytics ?? false;
}

syncSession();
watch(() => route.fullPath, syncSession);

const isUserFree = computed(() => !isUserGuest.value && !canUseAnalytics.value);
const isOverhaulAvailable = computed(() => {
  if (isUserFree.value) {
    const isSettingPage = route.fullPath.includes("/app/settings/");

    return !isSettingPage;
  }

  const isPaidUser = !isUserGuest.value;

  if (isPaidUser) {
    return AVAILABLE_OVERHAUL_PAGES_PAID
      .some((availablePage) => route.fullPath.includes(availablePage));
  }

  return true;
});

const isOverhaulEnabled = computed(() => store?.getters.isOverhaulEnabled && isOverhaulAvailable.value);
const isOrganizationRoute = computed(() => route.fullPath.startsWith("/app/organizations"));
</script>

<template>
  <div
    v-if="isOverhaulEnabled"
    class="application-view _app-shell"
    :class="{
      'application-view--ghost': isNavbarFloating,
      'application-view--contained': isOrganizationRoute && !state.isFooterVisible,
    }"
  >
    <OverlayWrapper />
    <NotifyWrapper />
    <CookieConsent />
    <ApplicationNavbar
      v-show="state.isNavbarVisible"
      class="application-view__navbar"
      :variant="state.navbarVariant"
    />

    <main class="application-view__content">
      <RouterView
        @toggle-navbar="onToggleNavbarVisibility"
        @toggle-footer="onToggleFooterVisibility"
        @navbar-variant-set="onNavbarVariantSet"
      />
      <Popover>
        <RouterView name="right" />
      </Popover>
    </main>

    <ApplicationFooter
      v-show="state.isFooterVisible"
      class="application-view__footer"
    />
  </div>
  <App v-else />
</template>

<style lang="scss" scoped>
.application-view {
  $self: &;

  display: flex;
  flex-flow: column;
  min-height: 100dvh;

  &__content {
    display: flex;
    flex-flow: column;
    flex: 1;

    & > * {
      flex: 1;
    }
  }

  &--ghost {
    position: relative;
  }

  &--contained {
    height: 100dvh;
    overflow: hidden;

    #{ $self }__content {
      min-height: 0;
      overflow-y: hidden;
    }
  }
}
</style>
